import { Button, MenuItem, Select } from '@mui/material';
import LoadingComponent from 'components/LoadingComponent';
import { Formik } from 'formik';
import _ from 'lodash';
import * as React from 'react';
import { PlaidLinkOnSuccessMetadata, usePlaidLink } from 'react-plaid-link';
import axiosWithToken from 'services/clients/axios-with-token';
import { ReactComponent as BankAccountLogo } from 'assets/logos/bank_account.svg';
import { BankAgreement } from './BankAgreement';
import BillingDetails, {
  BillingDetailsValues,
  initialBillingDetails,
  validateBillingDetails,
} from '../BillingDetails';
import { BankFormIsValid, OnFormIsValid, OnFormIsValidType } from '../interfaces';
import PaymentMethodItem from '../PaymentMethodItem';

interface LoadingState {
  status: 'loading';
}
interface SuccessState {
  status: 'success';
  token: string;
}
interface ErrorState {
  status: 'error';
  error: unknown;
}

type TokenState = LoadingState | SuccessState | ErrorState;

interface Props {
  onFormIsValid: OnFormIsValid;
}

interface BankValues extends BillingDetailsValues {
  selectedBankAccount: string;
}

/**
 * Renders the form for adding a new bank account to the user's account
 */
export const NewBankForm: React.FC<Props> = (props) => {
  const { onFormIsValid } = props;

  // State around retrieving the Plaid token
  const [tokenState, setTokenState] = React.useState<TokenState>({ status: 'loading' });
  const [displayMandate, setDisplayMandate] = React.useState<boolean>(false);
  // State returned by Plaid after successfully logging into a bank
  const [plaidData, setPlaidData] = React.useState<
    | {
        token: string;
        metadata: PlaidLinkOnSuccessMetadata;
      }
    | undefined
  >();

  // Clear the form on component mount
  React.useEffect(() => {
    onFormIsValid(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Retrieve the plaid token from our server
  React.useEffect(() => {
    const getToken = async () => {
      try {
        const token = (await axiosWithToken('/core/api/v1/auth/create-plaid-link-token')).data
          .link_token;
        setTokenState({ status: 'success', token });
      } catch (error) {
        setTokenState({ status: 'error', error });
      }
    };
    getToken();
  }, [setTokenState]);

  // Allow the user to select a bank account
  const { open, ready } = usePlaidLink({
    token: tokenState.status === 'success' ? tokenState.token : '',
    onSuccess: (token, metadata) => {
      setPlaidData({ token, metadata });
    },
  });

  // We'll be considered loading if we haven't gotten the token yet
  // or plaid isn't ready
  const isLoading = !ready || tokenState.status === 'loading';

  const initialValues: BankValues = {
    ...initialBillingDetails,
    selectedBankAccount: plaidData?.metadata.accounts[0]?.id || '',
  };

  const validate = React.useCallback(
    (values: BankValues) => {
      const errors = { ...validateBillingDetails(values) };

      const account = plaidData?.metadata.accounts.find((a) => a.id === values.selectedBankAccount);

      const valid = _.isEmpty(errors) && account !== undefined;

      const data: BankFormIsValid = {
        ...values,
        type: OnFormIsValidType.NEW_BANK,
        plaidAccountId: account?.id || '',
        plaidToken: plaidData?.token || '',
      };

      onFormIsValid(valid, data);

      return errors;
    },
    [plaidData, onFormIsValid]
  );

  return (
    <LoadingComponent isLoading={isLoading}>
      <Formik
        onSubmit={() => {}}
        initialValues={initialValues}
        validate={validate}
        enableReinitialize
      >
        {(formikProps) => (
          <div className="flex flex-col space-y-8 items-start w-full">
            <div className="flex flex-col space-y-4 items-start w-full">
              <h3 className="font-bold">Bank Account</h3>
              {!plaidData && (
                <Button
                  variant="contained"
                  startIcon={<BankAccountLogo width={17} height={17} fill="white" stroke="white" />}
                  disabled={!ready || displayMandate || tokenState.status === 'error'}
                  onClick={() => setDisplayMandate(true)}
                >
                  Link Bank Account
                </Button>
              )}
              {displayMandate && (
                <BankAgreement
                  accept={() => {
                    setDisplayMandate(false);
                    open();
                  }}
                />
              )}
              {plaidData && (
                <Select
                  name="selectedBankAccount"
                  variant="outlined"
                  disabled={plaidData.metadata.accounts.length === 0}
                  className="w-full"
                  sx={{
                    '& .MuiSelect-select': {
                      padding: 0,
                    },
                  }}
                  value={formikProps.values.selectedBankAccount}
                  onChange={formikProps.handleChange}
                  onBlur={formikProps.handleBlur}
                >
                  {plaidData.metadata.accounts.map((account) => (
                    <MenuItem key={account.id} value={account.id}>
                      <PaymentMethodItem
                        value={{
                          object: 'bank_account',
                          accountName: account.name,
                          bankName: plaidData.metadata.institution?.name || '',
                          last4: account.mask,
                          id: account.id,
                        }}
                      />
                    </MenuItem>
                  ))}
                </Select>
              )}
            </div>
            {plaidData && <BillingDetails {...formikProps} />}
          </div>
        )}
      </Formik>
    </LoadingComponent>
  );
};
NewBankForm.displayName = 'Checkout.NewBankForm';
